/**
 * Jubly — Workflow List Component
 * Displays all workflows with status, trigger type, run count, and last run.
 * Follows the same patterns as SalesCampaigns.jsx.
 */

import { useState, useEffect, useCallback } from 'react';
import { TRIGGER_TYPES, WORKFLOW_STATUSES } from '../constants/workflow.js';

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  draft:    { label: 'Draft',    bg: 'bg-gray-100',   text: 'text-gray-600',   dot: 'bg-gray-400' },
  active:   { label: 'Active',   bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500' },
  paused:   { label: 'Paused',   bg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500' },
  archived: { label: 'Archived', bg: 'bg-red-50',     text: 'text-red-500',    dot: 'bg-red-400' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function triggerLabel(triggerType) {
  const found = TRIGGER_TYPES.find(t => t.value === triggerType);
  return found ? found.label : (triggerType || '').replace(/_/g, ' ') || 'Unknown trigger';
}

function triggerGroup(triggerType) {
  const found = TRIGGER_TYPES.find(t => t.value === triggerType);
  return found?.group || 'System';
}

const GROUP_COLORS = {
  CRM:       'bg-blue-50 text-blue-700',
  Sales:     'bg-indigo-50 text-indigo-700',
  Billing:   'bg-emerald-50 text-emerald-700',
  Messaging: 'bg-sky-50 text-sky-700',
  System:    'bg-gray-100 text-gray-600',
};

function TriggerBadge({ triggerType }) {
  const group = triggerGroup(triggerType);
  const colorClass = GROUP_COLORS[group] || GROUP_COLORS.System;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${colorClass}`}>
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
      {triggerLabel(triggerType)}
    </span>
  );
}

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60)     return `${diff}s ago`;
  if (diff < 3600)   return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)  return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── New Workflow Modal ────────────────────────────────────────────────────────

function NewWorkflowModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [triggerType, setTriggerType] = useState('manual');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Group trigger types for the <select>
  const grouped = TRIGGER_TYPES.reduce((acc, t) => {
    if (!acc[t.group]) acc[t.group] = [];
    acc[t.group].push(t);
    return acc;
  }, {});

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/workflows', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), description: description.trim(), triggerType }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const workflow = await res.json();
      onCreated(workflow);
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">New Workflow</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Workflow Name *</label>
            <input
              autoFocus
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. New Contact Welcome Sequence"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Trigger</label>
            <select
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              value={triggerType}
              onChange={e => setTriggerType(e.target.value)}
            >
              {Object.entries(grouped).map(([group, items]) => (
                <optgroup key={group} label={group}>
                  {items.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea
              rows={2}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What does this workflow do?"
            />
          </div>
          {error && (
            <p className="flex items-center gap-1.5 text-xs text-red-500">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2 transition-colors"
            >
              {saving && (
                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
              )}
              Create Workflow
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Stat Card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, iconPath, iconBg, iconColor }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
        <svg className={`w-4.5 h-4.5 ${iconColor}`} style={{ width: 18, height: 18 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d={iconPath} />
        </svg>
      </div>
      <div>
        <p className="text-lg font-bold text-gray-900 leading-none">{value}</p>
        <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      </div>
    </div>
  );
}

// ── Workflow Row ──────────────────────────────────────────────────────────────

function WorkflowRow({ workflow, onStatusChange, onDelete, onSelect }) {
  const [actioning, setActioning] = useState(false);

  async function changeStatus(newStatus, e) {
    e.stopPropagation();
    setActioning(true);
    try {
      const res = await fetch(`/api/workflows/${workflow.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onStatusChange(workflow.id, newStatus);
    } catch (err) {
      console.error('Status change failed:', err);
    } finally {
      setActioning(false);
    }
  }

  async function handleDelete(e) {
    e.stopPropagation();
    if (!window.confirm(`Delete "${workflow.name}"? This cannot be undone.`)) return;
    try {
      await fetch(`/api/workflows/${workflow.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      onDelete(workflow.id);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  }

  const lastRunText = workflow.last_run_at ? timeAgo(workflow.last_run_at) : 'Never';
  const runCount = workflow.run_count ?? workflow.total_runs ?? 0;

  return (
    <li
      onClick={() => onSelect(workflow)}
      className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors group"
    >
      {/* Icon */}
      <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
        <svg className="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      </div>

      {/* Main info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-gray-900 truncate">{workflow.name}</p>
          <StatusBadge status={workflow.status} />
        </div>
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          <TriggerBadge triggerType={workflow.trigger_type} />
          {workflow.description && (
            <span className="text-xs text-gray-400 truncate hidden sm:block max-w-xs">
              {workflow.description}
            </span>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="hidden md:flex items-center gap-6 flex-shrink-0 text-right">
        <div>
          <p className="text-sm font-semibold text-gray-800">{runCount.toLocaleString()}</p>
          <p className="text-[11px] text-gray-400">runs</p>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-600">{lastRunText}</p>
          <p className="text-[11px] text-gray-400">last run</p>
        </div>
      </div>

      {/* Actions — visible on hover */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
        {actioning ? (
          <svg className="w-4 h-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        ) : (
          <>
            {workflow.status === 'draft' && (
              <button
                onClick={e => changeStatus('active', e)}
                title="Activate"
                className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            )}
            {workflow.status === 'active' && (
              <button
                onClick={e => changeStatus('paused', e)}
                title="Pause"
                className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            )}
            {workflow.status === 'paused' && (
              <button
                onClick={e => changeStatus('active', e)}
                title="Resume"
                className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            )}
            {workflow.status !== 'archived' && (
              <button
                onClick={e => changeStatus('archived', e)}
                title="Archive"
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </button>
            )}
            {workflow.status === 'archived' && (
              <button
                onClick={e => changeStatus('draft', e)}
                title="Restore"
                className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            )}
            <button
              onClick={handleDelete}
              title="Delete"
              className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </>
        )}
        <svg className="w-4 h-4 text-gray-300 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </li>
  );
}

// ── Main WorkflowList Component ───────────────────────────────────────────────

/**
 * WorkflowList
 *
 * Props:
 *   onSelect(workflow) — called when a workflow row is clicked (for drill-down)
 *
 * If onSelect is not provided, clicking a row is a no-op.
 */
export default function WorkflowList({ onSelect }) {
  const [workflows, setWorkflows]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch]             = useState('');
  const [showNew, setShowNew]           = useState(false);

  const fetchWorkflows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (statusFilter) params.set('status', statusFilter);
      const res = await fetch(`/api/workflows?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setWorkflows(data.data?.workflows || data.workflows || (Array.isArray(data) ? data : []));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchWorkflows(); }, [fetchWorkflows]);

  function handleStatusChange(id, newStatus) {
    setWorkflows(prev =>
      prev.map(w => w.id === id ? { ...w, status: newStatus } : w)
    );
  }

  function handleDelete(id) {
    setWorkflows(prev => prev.filter(w => w.id !== id));
  }

  // Client-side search filter
  const filtered = workflows.filter(w => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (w.name || '').toLowerCase().includes(q) ||
      (w.description || '').toLowerCase().includes(q) ||
      triggerLabel(w.trigger_type).toLowerCase().includes(q)
    );
  });

  // Summary stats
  const totalActive = workflows.filter(w => w.status === 'active').length;
  const totalPaused = workflows.filter(w => w.status === 'paused').length;
  const totalRuns   = workflows.reduce((sum, w) => sum + (w.run_count ?? w.total_runs ?? 0), 0);

  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* ── Toolbar ── */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-white flex-shrink-0 flex-wrap">
        <h1 className="text-base font-semibold text-gray-900 mr-1">Workflows</h1>

        {/* Search */}
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <input
            className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
            placeholder="Search workflows\u2026"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
          </svg>
        </div>

        {/* Status filter */}
        <select
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          {WORKFLOW_STATUSES.map(s => (
            <option key={s.value || s} value={s.value || s}>
              {(s.label || s.value || s).charAt ? (s.label || s).charAt(0).toUpperCase() + (s.label || s).slice(1) : String(s)}
            </option>
          ))}
        </select>

        {/* New button */}
        <button
          onClick={() => setShowNew(true)}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors flex-shrink-0"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          New Workflow
        </button>
      </div>

      {/* ── Stats row ── */}
      {!loading && !error && workflows.length > 0 && (
        <div className="grid grid-cols-3 gap-3 px-6 py-3 bg-gray-50/60 border-b border-gray-100 flex-shrink-0">
          <StatCard
            label="Active"
            value={totalActive}
            iconPath="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            iconBg="bg-green-50"
            iconColor="text-green-600"
          />
          <StatCard
            label="Paused"
            value={totalPaused}
            iconPath="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
            iconBg="bg-amber-50"
            iconColor="text-amber-600"
          />
          <StatCard
            label="Total Runs"
            value={totalRuns.toLocaleString()}
            iconPath="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            iconBg="bg-indigo-50"
            iconColor="text-indigo-600"
          />
        </div>
      )}

      {/* ── List body ── */}
      <div className="flex-1 overflow-y-auto bg-gray-50/40">

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center h-40">
            <svg className="w-6 h-6 animate-spin text-indigo-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center h-40 text-center px-6">
            <svg className="w-8 h-8 text-red-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm font-medium text-gray-700">Failed to load workflows</p>
            <p className="text-xs text-gray-400 mt-1">{error}</p>
            <button
              onClick={fetchWorkflows}
              className="mt-3 text-sm text-indigo-600 hover:underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center h-40 text-gray-400">
            <svg className="w-10 h-10 mb-3 stroke-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {workflows.length === 0 ? (
              <>
                <p className="text-sm font-medium text-gray-600">No workflows yet</p>
                <p className="text-xs mt-1 text-gray-400">Automate your CRM, messaging, and more</p>
                <button
                  onClick={() => setShowNew(true)}
                  className="mt-4 flex items-center gap-1.5 text-sm text-indigo-600 hover:underline"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create your first workflow
                </button>
              </>
            ) : (
              <>
                <p className="text-sm">No workflows match your search</p>
                <button
                  onClick={() => { setSearch(''); setStatusFilter(''); }}
                  className="mt-2 text-xs text-indigo-600 hover:underline"
                >
                  Clear filters
                </button>
              </>
            )}
          </div>
        )}

        {/* Workflow rows */}
        {!loading && !error && filtered.length > 0 && (
          <ul className="divide-y divide-gray-100 bg-white">
            {filtered.map(workflow => (
              <WorkflowRow
                key={workflow.id}
                workflow={workflow}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
                onSelect={onSelect || (() => {})}
              />
            ))}
          </ul>
        )}
      </div>

      {/* ── New Workflow Modal ── */}
      {showNew && (
        <NewWorkflowModal
          onClose={() => setShowNew(false)}
          onCreated={newWorkflow => {
            setWorkflows(prev => [newWorkflow, ...prev]);
            if (onSelect) onSelect(newWorkflow);
          }}
        />
      )}
    </div>
  );
}
